import { useCallback, useEffect, useRef, useState } from "react";

export const MAX_SECONDS = 6;

export function useVideoRecorder(onCapture) {
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const recorderRef = useRef(null);
  const timerRef = useRef(null);
  const generation = useRef(0);
  const captureRef = useRef(onCapture);
  captureRef.current = onCapture;
  const [phase, setPhase] = useState("idle");
  const [error, setError] = useState("");
  const [countdown, setCountdown] = useState(0);
  const [elapsed, setElapsed] = useState(0);

  const release = useCallback(() => {
    clearInterval(timerRef.current);
    streamRef.current?.getTracks().forEach((track) => track.stop());
    streamRef.current = null;
    // srcObject takes precedence over src, even after every track has ended.
    if (videoRef.current) videoRef.current.srcObject = null;
  }, []);

  const cancel = useCallback(() => {
    generation.current += 1;
    const recorder = recorderRef.current;
    if (recorder) {
      recorder.onstop = null;
      recorder.ondataavailable = null;
      recorder.onerror = null;
      if (recorder.state !== "inactive") recorder.stop();
    }
    release();
  }, [release]);

  useEffect(() => cancel, [cancel]);

  const start = async () => {
    cancel();
    const current = generation.current;
    setError("");
    setPhase("starting");
    setElapsed(0);
    setCountdown(0);
    try {
      if (!navigator.mediaDevices?.getUserMedia || !window.MediaRecorder) {
        throw new Error("unsupported");
      }
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: "user", width: { ideal: 640 }, height: { ideal: 480 }, frameRate: { ideal: 24, max: 30 } },
        audio: true,
      });
      if (generation.current !== current) {
        stream.getTracks().forEach((track) => track.stop());
        return;
      }
      streamRef.current = stream;
      videoRef.current.srcObject = stream;
      await videoRef.current.play();
      if (generation.current === current) setPhase("ready");
    } catch (err) {
      if (generation.current !== current) return;
      release();
      setPhase("idle");
      setError(err.name === "NotAllowedError"
        ? "Camera or microphone permission was denied. Allow both in your browser settings, then try again."
        : err.name === "NotFoundError"
        ? "No camera or microphone was found. Please use a device with both."
        : err.message === "unsupported"
        ? "This browser cannot record video. Open this page in a current Safari, Chrome or Firefox browser."
        : "Could not start the camera. Close other camera apps, then try again.");
    }
  };

  const stopRecording = () => {
    clearInterval(timerRef.current);
    const recorder = recorderRef.current;
    if (recorder?.state === "recording") {
      setPhase("processing");
      // Wait for the final dataavailable + stop events before releasing tracks.
      recorder.stop();
    }
  };

  const beginRecording = () => {
    try {
      const formats = ['video/mp4;codecs=avc1.42E01E,mp4a.40.2', 'video/mp4', 'video/webm;codecs=vp8,opus', 'video/webm'];
      const mimeType = formats.find((type) => MediaRecorder.isTypeSupported(type));
      const recorder = new MediaRecorder(streamRef.current, { ...(mimeType ? { mimeType } : {}), videoBitsPerSecond: 800000 });
      recorderRef.current = recorder;
      const chunks = [];
      const current = generation.current;
      const started = Date.now();
      recorder.ondataavailable = ({ data }) => { if (data.size) chunks.push(data); };
      recorder.onerror = () => {
        cancel();
        setPhase("idle");
        setError("Recording was interrupted. Please start the camera and record again.");
      };
      recorder.onstop = () => {
        if (generation.current !== current) return;
        const blob = new Blob(chunks, { type: recorder.mimeType || chunks[0]?.type });
        release();
        setPhase("idle");
        if (blob.size < 1024 || Date.now() - started < 1800) {
          setError("The recording was too short. Please record the full code again.");
          return;
        }
        captureRef.current(blob);
      };
      recorder.start(250);
      setPhase("recording");
      timerRef.current = setInterval(() => {
        const seconds = Math.min(MAX_SECONDS, (Date.now() - started) / 1000);
        setElapsed(seconds);
        if (seconds >= MAX_SECONDS) stopRecording();
      }, 100);
    } catch {
      cancel();
      setPhase("idle");
      setError("Your browser could not start recording. Try a current Safari, Chrome or Firefox browser.");
    }
  };

  const beginCountdown = () => {
    if (phase !== "ready") return;
    setError("");
    setPhase("countdown");
    let remaining = 3;
    setCountdown(remaining);
    timerRef.current = setInterval(() => {
      remaining -= 1;
      setCountdown(remaining);
      if (!remaining) {
        clearInterval(timerRef.current);
        beginRecording();
      }
    }, 1000);
  };

  return { videoRef, phase, error, setError, countdown, elapsed, start, beginCountdown, stopRecording };
}
